// ===========================================
// PREMIUM SETTINGS PAGE - VP-Level Design
// Matching Tripnovac Platform Hub Quality
// ===========================================

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  User,
  Bell,
  Lock,
  Mail,
  CreditCard,
  Building,
  Globe,
  Palette,
  Zap,
  Shield,
  Key,
  Smartphone,
  Camera,
  Save,
  Check,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  Download,
  RefreshCw,
  Link,
  MessageSquare,
  Loader2,
  UserPlus,
} from 'lucide-react';
import { integrationsService } from '../../services';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import { toast } from 'react-hot-toast';
import emailConfig from '../../lib/emailConfig';
import { useNavigate } from 'react-router-dom';
import { usersService } from '../../services/users.service';

// Subscription and billing types
interface SubscriptionData {
  subscriptionTier: string;
  subscriptionStatus: string;
  stripeCustomerId?: string;
  maxUsers: number;
  maxContacts: number;
  maxEmailsPerMonth: number;
  features: string[];
}

interface UsageData {
  users: number;
  contacts: number;
  bookings: number;
}

interface SettingsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const settingsTabs: SettingsTab[] = [
  { id: 'profile', label: 'Profile', icon: <User className="w-4 h-4" />, description: 'Your personal information' },
  { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" />, description: 'Email & push preferences' },
  { id: 'security', label: 'Security', icon: <Lock className="w-4 h-4" />, description: 'Password & 2FA settings' },
  { id: 'email', label: 'Email Settings', icon: <Mail className="w-4 h-4" />, description: 'SMTP & sender config' },
  { id: 'billing', label: 'Billing', icon: <CreditCard className="w-4 h-4" />, description: 'Subscription & invoices' },
  { id: 'team', label: 'Team', icon: <Building className="w-4 h-4" />, description: 'Organization settings' },
  { id: 'integrations', label: 'Integrations', icon: <Zap className="w-4 h-4" />, description: 'Third-party connections' },
  { id: 'appearance', label: 'Appearance', icon: <Palette className="w-4 h-4" />, description: 'Theme & display' },
];

const PremiumSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [calendlyConnected, setCalendlyConnected] = useState<boolean | null>(null);
  const [integrationHealth, setIntegrationHealth] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [inviteEmailQuick, setInviteEmailQuick] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState<{ qrCode: string; secret: string } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorBackupCodes, setTwoFactorBackupCodes] = useState<string[]>([]);
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  
  // Subscription and billing states
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billedByHub, setBilledByHub] = useState(false);
  
  // Form states - initialized from authenticated user
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timezone: 'America/New_York',
    language: 'en',
    company: '',
    role: '',
  });

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        timezone: user.timezone || 'America/New_York',
        language: user.language || 'en',
        company: user.organization?.name || user.company || '',
        role: user.role || '',
      });
      setLoading(false);
      setTwoFactorEnabled(Boolean((user as any).twoFactorEnabled));
    }
  }, [user]);

  const [notifications, setNotifications] = useState({
    emailMarketing: true,
    emailSecurity: true,
    emailUpdates: false,
    pushCampaigns: true,
    pushContacts: true,
    pushReports: false,
    weeklyDigest: true,
    monthlyReport: true,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      // Call real API to update profile
      await updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: profileData.phone,
        timezone: profileData.timezone,
        language: profileData.language,
      });
      setSaved(true);
      toast.success('Settings saved successfully!');
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const loadIntegrationStatus = async () => {
      try {
        const [calendlyRes, allRes] = await Promise.all([
          integrationsService.getCalendlyStatus().catch(() => null),
          integrationsService.getAllIntegrationsStatus().catch(() => null),
        ]);
        const calendlyData = (calendlyRes as any)?.data || calendlyRes;
        setCalendlyConnected(Boolean(calendlyData?.connected));

        const allData = (allRes as any)?.data || allRes;
        const integrations = allData?.integrations || {};
        setIntegrationHealth({
          sendgrid: Boolean(integrations.sendgrid?.configured),
          twilio: Boolean(integrations.twilio?.configured),
          openai: Boolean(integrations.openai?.configured),
          stripe: Boolean(integrations.stripe?.configured),
          cloudinary: Boolean(integrations.cloudinary?.configured),
          calendly: Boolean(integrations.calendly?.configured),
        });
      } catch {
        setCalendlyConnected(false);
        setIntegrationHealth({});
      }
    };

    loadIntegrationStatus();
  }, []);

  // Fetch subscription data when billing tab is active
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (activeTab !== 'billing') return;
      setBillingLoading(true);
      try {
        const response = await api.get('/api/billing/subscription');
        const data = (response as any).data || response;
        if (data.subscription) {
          setSubscription(data.subscription);
        }
        if (data.usage) {
          setUsage(data.usage);
        }
        setBilledByHub(Boolean(data.billedByHub || (response as any).billedByHub));
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      } finally {
        setBillingLoading(false);
      }
    };
    fetchSubscriptionData();
  }, [activeTab]);

  // Open Stripe billing portal
  const handleManageBilling = async () => {
    if (billedByHub) {
      toast.error('This organization is billed through Tripnovac Hub.');
      return;
    }
    try {
      setBillingLoading(true);
      const response = await api.post('/api/billing/subscription/portal');
      const data = (response as any).data || response;
      if (data.portalUrl) {
        window.location.href = data.portalUrl;
      } else {
        toast.error('Unable to open billing portal');
      }
    } catch (err) {
      toast.error('Failed to open billing portal. Please try again.');
    } finally {
      setBillingLoading(false);
    }
  };

  const renderProfileSettings = () => (
    <div className="space-y-6">
      {/* Profile Photo */}
      <div className="flex items-start gap-6 pb-6 border-b border-gray-200">
        <div className="relative">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
            {profileData.firstName?.[0] || ''}{profileData.lastName?.[0] || ''}
          </div>
          <button className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors">
            <Camera className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Profile Photo</h3>
          <p className="text-sm text-gray-500 mb-3">PNG or JPG, max 2MB</p>
          <div className="flex gap-2">
            <button className="px-3 py-1.5 rounded-lg bg-violet-50 text-violet-700 text-sm font-medium hover:bg-violet-100 transition-colors">
              <Upload className="w-4 h-4 inline mr-1" />
              Upload
            </button>
            <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
              <Trash2 className="w-4 h-4 inline mr-1" />
              Remove
            </button>
          </div>
        </div>
      </div>

      {/* Personal Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
          <input
            type="text"
            value={profileData.company}
            onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
          <input
            type="text"
            value={profileData.role}
            onChange={(e) => setProfileData({ ...profileData, role: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
          <select
            value={profileData.timezone}
            onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all bg-white"
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Paris">Paris (CET)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
          <select
            value={profileData.language}
            onChange={(e) => setProfileData({ ...profileData, language: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all bg-white"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Email Notifications</h3>
        <div className="space-y-3">
          {[
            { key: 'emailMarketing', label: 'Marketing updates', desc: 'Receive emails about campaign performance' },
            { key: 'emailSecurity', label: 'Security alerts', desc: 'Get notified about account security events' },
            { key: 'emailUpdates', label: 'Product updates', desc: 'Learn about new features and updates' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
                className={`w-12 h-6 rounded-full transition-colors relative ${
                  notifications[item.key as keyof typeof notifications]
                    ? 'bg-violet-500'
                    : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                  notifications[item.key as keyof typeof notifications] ? 'right-1' : 'left-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Push Notifications — web push is not live; mobile Expo push is separate */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Push Notifications</h3>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-4">
          Browser web push is not enabled yet. These toggles only apply to the Tripnovac Sales mobile app (Expo). Email alerts above still apply on web.
        </p>
        <div className="space-y-3">
          {[
            { key: 'pushCampaigns', label: 'Campaign alerts', desc: 'Get notified when campaigns start or end' },
            { key: 'pushContacts', label: 'Contact updates', desc: 'New contacts and list changes' },
            { key: 'pushReports', label: 'Report alerts', desc: 'Weekly and monthly report notifications' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
                className={`w-12 h-6 rounded-full transition-colors relative ${
                  notifications[item.key as keyof typeof notifications]
                    ? 'bg-violet-500'
                    : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                  notifications[item.key as keyof typeof notifications] ? 'right-1' : 'left-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Reports */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Reports</h3>
        <div className="space-y-3">
          {[
            { key: 'weeklyDigest', label: 'Weekly digest', desc: 'Summary of your weekly performance' },
            { key: 'monthlyReport', label: 'Monthly report', desc: 'Detailed monthly analytics report' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
              <button
                onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key as keyof typeof notifications] })}
                className={`w-12 h-6 rounded-full transition-colors relative ${
                  notifications[item.key as keyof typeof notifications]
                    ? 'bg-violet-500'
                    : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                  notifications[item.key as keyof typeof notifications] ? 'right-1' : 'left-1'
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      {/* Password */}
      <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-violet-500" />
          Change Password
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all bg-white"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition-all bg-white"
            />
          </div>
          <button
            type="button"
            disabled={passwordBusy}
            onClick={async () => {
              if (!currentPassword || !newPassword) {
                toast.error('Enter current and new password');
                return;
              }
              if (newPassword !== confirmPassword) {
                toast.error('New passwords do not match');
                return;
              }
              if (newPassword.length < 8) {
                toast.error('Password must be at least 8 characters');
                return;
              }
              try {
                setPasswordBusy(true);
                await usersService.changePassword(currentPassword, newPassword);
                toast.success('Password updated');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              } catch (err: any) {
                toast.error(err?.response?.data?.error || err?.message || 'Failed to update password');
              } finally {
                setPasswordBusy(false);
              }
            }}
            className="px-4 py-2 rounded-xl bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors disabled:opacity-50"
          >
            {passwordBusy ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Two-Factor Authentication</h3>
              <p className="text-sm text-gray-500 mt-1">Add an extra layer of security to your account</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  twoFactorEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {twoFactorEnabled ? 'Enabled' : 'Not Enabled'}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            disabled={twoFactorBusy}
            onClick={async () => {
              try {
                setTwoFactorBusy(true);
                if (twoFactorEnabled) {
                  const code = window.prompt('Enter a 2FA code to disable two-factor authentication');
                  if (!code) return;
                  await usersService.disable2FA(code);
                  setTwoFactorEnabled(false);
                  setTwoFactorSetup(null);
                  toast.success('2FA disabled');
                  return;
                }
                const response: any = await usersService.enable2FA();
                const data = response?.data || response;
                setTwoFactorSetup({ qrCode: data.qrCode, secret: data.secret || data.manualEntryKey });
                toast.success('Scan the QR code, then enter a 6-digit code to finish setup');
              } catch (err: any) {
                toast.error(err?.response?.data?.error || err?.message || '2FA request failed');
              } finally {
                setTwoFactorBusy(false);
              }
            }}
            className={`px-4 py-2 rounded-xl text-white font-medium transition-colors disabled:opacity-50 ${
              twoFactorEnabled ? 'bg-gray-700 hover:bg-gray-800' : 'bg-emerald-500 hover:bg-emerald-600'
            }`}
          >
            {twoFactorBusy ? 'Working…' : twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
          </button>
        </div>
        {twoFactorSetup && !twoFactorEnabled && (
          <div className="mt-6 p-4 rounded-xl bg-white border border-emerald-100 space-y-3">
            {twoFactorSetup.qrCode && (
              <img src={twoFactorSetup.qrCode} alt="2FA QR" className="w-40 h-40 mx-auto" />
            )}
            <p className="text-xs text-center text-gray-500 font-mono break-all">{twoFactorSetup.secret}</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-3 text-center text-2xl tracking-widest rounded-xl border border-gray-200"
            />
            <button
              type="button"
              disabled={twoFactorBusy || twoFactorCode.length !== 6}
              onClick={async () => {
                try {
                  setTwoFactorBusy(true);
                  const response: any = await usersService.verify2FA(twoFactorCode);
                  const data = response?.data || response;
                  setTwoFactorEnabled(true);
                  setTwoFactorBackupCodes(data.backupCodes || []);
                  setTwoFactorCode('');
                  toast.success('Two-factor authentication enabled');
                } catch (err: any) {
                  toast.error(err?.response?.data?.message || err?.message || 'Invalid code');
                } finally {
                  setTwoFactorBusy(false);
                }
              }}
              className="w-full px-4 py-2 rounded-xl bg-emerald-500 text-white font-medium disabled:opacity-50"
            >
              Verify and enable
            </button>
            {twoFactorBackupCodes.length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-100 rounded-xl p-3">
                <p className="font-semibold text-amber-800 mb-2">Save these backup codes now — they will not be shown again.</p>
                <div className="grid grid-cols-2 gap-1 font-mono">
                  {twoFactorBackupCodes.map((c) => (
                    <span key={c}>{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-violet-500" />
          Active Sessions
        </h3>
        <div className="flex items-center justify-between p-4 rounded-xl bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-100">
              <Monitor className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                Current Session
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Active</span>
              </p>
              <p className="text-xs text-gray-500">This browser • Just now</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Session management across devices will be available in a future update.
        </p>
      </div>
    </div>
  );

  const renderBillingSettings = () => {
    // Plan pricing mapping
    const planPricing: Record<string, { price: number; name: string }> = {
      FREE: { price: 0, name: 'Free' },
      STARTER: { price: 29, name: 'Starter' },
      GROWTH: { price: 79, name: 'Growth' },
      BUSINESS: { price: 199, name: 'Business' },
    };

    const currentPlan = planPricing[subscription?.subscriptionTier || 'FREE'] || planPricing.FREE;

    if (billingLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Current Plan */}
        <div className="p-6 rounded-2xl border-2 border-violet-500 bg-gradient-to-br from-violet-50 to-fuchsia-50">
          <div className="flex items-start justify-between">
            <div>
              <span className="inline-flex items-center px-2 py-1 rounded-lg bg-violet-500 text-white text-xs font-bold">
                CURRENT PLAN
              </span>
              <span
                className={`ml-2 inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${
                  billedByHub ? 'bg-emerald-700 text-white' : 'bg-slate-700 text-white'
                }`}
              >
                {billedByHub ? 'Hub' : 'app_local'}
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">{currentPlan.name}</h3>
              <p className="text-gray-600 mt-1">
                Status: <span className={`font-medium ${subscription?.subscriptionStatus === 'ACTIVE' ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {subscription?.subscriptionStatus || 'Unknown'}
                </span>
              </p>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">${currentPlan.price}</span>
                <span className="text-gray-500">/month</span>
              </div>
            </div>
            <button 
              onClick={handleManageBilling}
              disabled={billingLoading || billedByHub}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2"
            >
              {billingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Manage Billing
            </button>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="p-3 rounded-xl bg-white/60">
              <p className="text-xs text-gray-500">Contacts</p>
              <p className="text-sm font-bold text-gray-900">
                {usage?.contacts?.toLocaleString() || 0} / {subscription?.maxContacts?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/60">
              <p className="text-xs text-gray-500">Emails/month</p>
              <p className="text-sm font-bold text-gray-900">
                {subscription?.maxEmailsPerMonth?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/60">
              <p className="text-xs text-gray-500">Team members</p>
              <p className="text-sm font-bold text-gray-900">
                {usage?.users || 0} / {subscription?.maxUsers || 0}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/60">
              <p className="text-xs text-gray-500">Bookings</p>
              <p className="text-sm font-bold text-gray-900">
                {usage?.bookings || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Payment & Invoices via Stripe Portal */}
        <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-500" />
            Payment Method & Invoices
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Manage your payment methods, view invoices, and download receipts through the Stripe billing portal.
          </p>
          <button 
            onClick={handleManageBilling}
            disabled={billingLoading || billedByHub}
            className="px-4 py-2 rounded-xl bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors flex items-center gap-2"
          >
            {billingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Open Billing Portal
          </button>
        </div>
      </div>
    );
  };

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Platform-level services (Stripe, SendGrid, Twilio, OpenAI, Cloudinary) are configured by Tripnovac ops — status below is live from the API. Calendly is connected per organization via OAuth.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { id: 'calendly', name: 'Calendly', icon: <Link className="w-6 h-6" />, connected: calendlyConnected ?? integrationHealth.calendly ?? false, color: 'from-blue-500 to-indigo-600', configurable: true, hint: 'Connect your Calendly account to sync bookings' },
          { id: 'stripe', name: 'Stripe', icon: <CreditCard className="w-6 h-6" />, connected: integrationHealth.stripe ?? !!subscription?.stripeCustomerId, color: 'from-purple-500 to-indigo-600', configurable: false, hint: 'Platform billing — managed in Billing' },
          { id: 'sendgrid', name: 'SendGrid', icon: <Mail className="w-6 h-6" />, connected: integrationHealth.sendgrid ?? false, color: 'from-blue-500 to-blue-600', configurable: false, hint: 'Platform email sending' },
          { id: 'twilio', name: 'Twilio', icon: <MessageSquare className="w-6 h-6" />, connected: integrationHealth.twilio ?? false, color: 'from-red-500 to-red-600', configurable: false, hint: 'Platform SMS / WhatsApp' },
          { id: 'openai', name: 'OpenAI', icon: <Zap className="w-6 h-6" />, connected: integrationHealth.openai ?? false, color: 'from-emerald-500 to-emerald-600', configurable: false, hint: 'Platform AI assistant' },
          { id: 'cloudinary', name: 'Cloudinary', icon: <Upload className="w-6 h-6" />, connected: integrationHealth.cloudinary ?? false, color: 'from-sky-500 to-blue-600', configurable: false, hint: 'Platform media uploads' },
        ].map((integration) => (
          <div key={integration.id} className="p-5 rounded-2xl border border-gray-200 bg-white hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${integration.color} flex items-center justify-center text-white shadow-lg`}>
                  {integration.icon}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{integration.name}</p>
                  <p className={`text-xs ${integration.connected ? 'text-emerald-600' : 'text-gray-500'}`}>
                    {integration.connected ? '✓ Configured' : 'Not configured'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{integration.hint}</p>
                </div>
              </div>
              {integration.configurable ? (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (integration.connected) {
                        await integrationsService.disconnectCalendly();
                        setCalendlyConnected(false);
                        toast.success('Calendly disconnected');
                        return;
                      }
                      const response: any = await integrationsService.getCalendlyAuthUrl();
                      const url = response?.authorizationUrl || response?.data?.authorizationUrl;
                      if (!url) {
                        toast.error('Calendly OAuth is not configured on the server. Add CALENDLY_CLIENT_ID / SECRET.');
                        return;
                      }
                      window.location.href = url;
                    } catch (err: any) {
                      toast.error(err?.response?.data?.error || err?.message || 'Calendly connect failed');
                    }
                  }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    integration.connected
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-violet-500 text-white hover:bg-violet-600'
                  }`}
                >
                  {integration.connected ? 'Disconnect' : 'Connect'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (integration.id === 'stripe') navigate('/settings');
                    toast(
                      integration.connected
                        ? `${integration.name} is configured at the platform level.`
                        : `${integration.name} is not configured yet. Tripnovac ops must set the server key.`
                    );
                  }}
                  className="px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  {integration.connected ? 'Details' : 'Unavailable'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      {/* Theme */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { id: 'light', label: 'Light', icon: <Sun className="w-5 h-5" /> },
            { id: 'dark', label: 'Dark', icon: <Moon className="w-5 h-5" /> },
            { id: 'system', label: 'System', icon: <Monitor className="w-5 h-5" /> },
          ].map((theme) => (
            <button
              key={theme.id}
              className={`p-4 rounded-2xl border-2 transition-all ${
                theme.id === 'light'
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center ${
                theme.id === 'light' ? 'bg-violet-500 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {theme.icon}
              </div>
              <p className="text-sm font-medium text-gray-900">{theme.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Accent Color */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Accent Color</h3>
        <div className="flex gap-3">
          {[
            { color: 'bg-violet-500', name: 'Violet' },
            { color: 'bg-blue-500', name: 'Blue' },
            { color: 'bg-emerald-500', name: 'Emerald' },
            { color: 'bg-rose-500', name: 'Rose' },
            { color: 'bg-amber-500', name: 'Amber' },
            { color: 'bg-cyan-500', name: 'Cyan' },
          ].map((item, index) => (
            <button
              key={index}
              className={`w-10 h-10 rounded-xl ${item.color} ${
                index === 0 ? 'ring-2 ring-offset-2 ring-violet-500' : ''
              } hover:scale-110 transition-transform`}
              title={item.name}
            />
          ))}
        </div>
      </div>

      {/* Display Density */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Display Density</h3>
        <div className="space-y-3">
          {[
            { id: 'comfortable', label: 'Comfortable', desc: 'More spacing, easier to scan' },
            { id: 'compact', label: 'Compact', desc: 'Less spacing, more content visible' },
          ].map((option) => (
            <div key={option.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div>
                <p className="text-sm font-medium text-gray-900">{option.label}</p>
                <p className="text-xs text-gray-500">{option.desc}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                option.id === 'comfortable' ? 'border-violet-500 bg-violet-500' : 'border-gray-300'
              }`}>
                {option.id === 'comfortable' && <Check className="w-4 h-4 text-white" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Platform email (SendGrid)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Outbound email is sent with Tripnovac’s SendGrid account. Agencies do not enter their own SMTP credentials here.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">From Name</label>
            <input type="text" readOnly value="Tripnovac Sales" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-600" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">From Email</label>
            <input type="email" readOnly value={emailConfig.noreply} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-600" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Reply-To</label>
            <input type="email" readOnly value={emailConfig.support} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-600" />
          </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${integrationHealth.sendgrid ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
          <span className="text-sm text-gray-600">
            {integrationHealth.sendgrid ? 'SendGrid is configured on the platform' : 'SendGrid is not configured — invites and campaigns cannot send email yet'}
          </span>
        </div>
        <button
          type="button"
          disabled={testEmailBusy || !integrationHealth.sendgrid || !user?.email}
          onClick={async () => {
            try {
              setTestEmailBusy(true);
              await api.post('/api/integrations/sendgrid/send', {
                to: user?.email,
                subject: 'Tripnovac Sales test email',
                text: 'This is a test from Settings → Email.',
                html: '<p>This is a test from Settings → Email.</p>',
              });
              toast.success(`Test email sent to ${user?.email}`);
            } catch (err: any) {
              toast.error(err?.response?.data?.error || err?.message || 'Test email failed');
            } finally {
              setTestEmailBusy(false);
            }
          }}
          className="px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {testEmailBusy ? 'Sending…' : 'Send Test Email'}
        </button>
      </div>
    </div>
  );

  const renderTeamSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Team Members</h3>
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700"
          >
            Manage team
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            type="email"
            value={inviteEmailQuick}
            onChange={(e) => setInviteEmailQuick(e.target.value)}
            placeholder="colleague@agency.com"
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
          <button
            type="button"
            disabled={inviteBusy || !inviteEmailQuick.trim()}
            onClick={async () => {
              try {
                setInviteBusy(true);
                const result: any = await usersService.invite({
                  email: inviteEmailQuick.trim(),
                  role: 'MEMBER',
                });
                const data = result?.data || result;
                if (data?.emailSent === false && data?.temporaryPassword) {
                  toast.success(`User created. Email not sent — share this temp password: ${data.temporaryPassword}`);
                } else {
                  toast.success(data?.message || result?.message || 'Invitation sent');
                }
                setInviteEmailQuick('');
              } catch (err: any) {
                toast.error(err?.response?.data?.error || err?.message || 'Invite failed — check seat limit or try Users page');
              } finally {
                setInviteBusy(false);
              }
            }}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
          >
            <UserPlus className="w-4 h-4" />
            {inviteBusy ? 'Sending…' : 'Invite Member'}
          </button>
        </div>
        <div className="space-y-3">
          {[
            { name: user?.firstName + ' ' + user?.lastName, email: user?.email, role: 'Owner', status: 'Active' },
          ].map((member, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-medium">
                  {member.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{member.name || 'User'}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  member.role === 'Owner' ? 'bg-violet-100 text-violet-700' :
                  member.role === 'Admin' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {member.role}
                </span>
                <span className="text-sm text-emerald-600">● {member.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Organization Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization Name</label>
            <input
              type="text"
              defaultValue={user?.organization?.name || profileData.company || ''}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Industry</label>
            <select className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent">
              <option>Travel & Tourism</option>
              <option>Marketing Agency</option>
              <option>E-commerce</option>
              <option>SaaS</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Team Size</label>
            <select className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-violet-500 focus:border-transparent">
              <option>1-5</option>
              <option>6-20</option>
              <option>21-50</option>
              <option>51-200</option>
              <option>200+</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'email':
        return renderEmailSettings();
      case 'billing':
        return renderBillingSettings();
      case 'team':
        return renderTeamSettings();
      case 'integrations':
        return renderIntegrationSettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderProfileSettings();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white shadow-lg">
              <Settings className="w-6 h-6" />
            </div>
            Settings
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your account preferences and configuration
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all disabled:opacity-70"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="w-4 h-4" />
              Saved!
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </motion.button>
      </div>

      {/* Main Content */}
      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-64 flex-shrink-0">
          <nav className="bg-white rounded-2xl border border-gray-200 p-3 shadow-sm sticky top-6">
            <div className="space-y-1">
              {settingsTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeTab === tab.id
                      ? 'bg-violet-50 text-violet-700 border border-violet-200'
                      : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className={`${activeTab === tab.id ? 'text-violet-500' : 'text-gray-400'}`}>
                    {tab.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{tab.label}</p>
                    <p className="text-xs text-gray-400 truncate">{tab.description}</p>
                  </div>
                  {activeTab === tab.id && (
                    <ChevronRight className="w-4 h-4 text-violet-500" />
                  )}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm"
          >
            <AnimatePresence mode="wait">
              {renderTabContent()}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PremiumSettingsPage;
